<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';
import contentLogo from 'dashboard/assets/images/contentlogo.png';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    const headerTitles = {
      clinical_dashboard: 'Dashboard Clínico',
      clinical_dashboard_overview: 'Dashboard Clínico',
      contacts_dashboard_index: 'Central de Pacientes',
      contacts_edit: 'Central de Pacientes',
      contacts_dashboard_active: 'Central de Pacientes',
      general_settings_index: 'Gestão',
      agent_list: 'Gestão',
      settings_teams_list: 'Gestão',
      settings_procedures_list: 'Gestão',
      settings_procedure_scores_list: 'Gestão',
      settings_procedure_scores_edit: 'Gestão',
      settings_physicians: 'Gestão',
      settings_inbox_list: 'Gestão',
      labels_list: 'Gestão',
      canned_list: 'Gestão',
      settings_digital_humans_list: 'Gestão',
      settings_llm_keys: 'Gestão',
      settings_knowledge_bases: 'Gestão',
      home: 'Central de Conversas',
      inbox_conversation: 'Central de Conversas',
      conversation_mentions: 'Central de Conversas',
      care_alerts_index: 'Alertas do Sistema',
      account_overview_reports: 'Relatórios e Analytics',
      conversation_reports: 'Relatórios e Analytics',
      agent_reports_index: 'Relatórios e Analytics',
      label_reports_index: 'Relatórios e Analytics',
      inbox_reports_index: 'Relatórios e Analytics',
      team_reports_index: 'Relatórios e Analytics',
      csat_reports: 'Relatórios e Analytics',
      sla_reports: 'Relatórios e Analytics',
      dh_reports: 'Relatórios e Analytics',
    };

    const headerSubtitles = {
      clinical_dashboard:
        'Visão integrada de performance: indicadores operacionais, métricas clínicas e tendências de recuperação.',
      clinical_dashboard_overview:
        'Visão integrada de performance: indicadores operacionais, métricas clínicas e tendências de recuperação.',
      contacts_dashboard_index:
        'Gerencie cadastros e acompanhamento de pacientes.',
      general_settings_index: 'Configurações da clínica, equipe e integrações.',
      home: 'Acompanhe interações com pacientes e intervenha quando necessário.',
      care_alerts_index:
        'Monitore alertas críticos, avisos e informações importantes sobre pacientes.',
      account_overview_reports:
        'Análise detalhada de performance, categorias e acompanhamento individual.',
    };

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      contentLogo,
      headerTitles,
      headerSubtitles,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    currentHeaderTitle() {
      return this.headerTitles[this.$route.name] || '';
    },
    currentHeaderSubtitle() {
      return this.headerSubtitles[this.$route.name] || '';
    },
    showTopHeader() {
      return !!this.currentHeaderTitle;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<!-- eslint-disable vue/no-static-inline-styles, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <div class="flex flex-col flex-1 h-full w-full min-h-0 overflow-hidden">
      <header v-if="showTopHeader" class="clara-top-header">
        <div class="clara-top-header-left">
          <div class="clara-top-header-brand">
            <img
              :src="contentLogo"
              alt="CLARA"
              style="height: 40px; width: auto"
            />
            <div class="flex-1 min-w-0">
              <div class="clara-top-header-title">{{ currentHeaderTitle }}</div>
              <div
                v-if="currentHeaderSubtitle"
                class="clara-top-header-subtitle"
              >
                {{ currentHeaderSubtitle }}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main
        class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
      >
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        >
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
        </UpgradePage>
        <template v-if="!showUpgradePage">
          <router-view />
          <CommandBar />
          <CopilotLauncher />
          <MobileSidebarLauncher
            :is-mobile-sidebar-open="isMobileSidebarOpen"
            @toggle="toggleMobileSidebar"
          />
          <CopilotContainer />
          <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
        </template>
        <AddAccountModal
          :show="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
      </main>
    </div>
  </div>
</template>
